import { useEffect, useState } from 'react';
import {
  useCreate,
  useMany,
  useNotification,
  useTranslate,
  useUpdate,
} from '@refinedev/core';
import { Form, Modal } from 'antd';
import type {
  OrganizationOut,
  PublisherListOut,
} from '@/api/generated/schemas';
import { ContractFormFields } from '@/components/tenancy/ContractFormFields';
import {
  buildPublisherTree,
  toPublisherTreeData,
  PUBLISHER_TREE_LIMIT,
} from '@/components/user_catalog/publishers/publisherTree';
import {
  CONTRACT_STARTER_RATE,
  toContractFormValues,
  toContractPayload,
} from '@/components/tenancy/contractFormValues';
import type { ContractFormValues } from '@/components/tenancy/contractFormValues';
import { useOrganizationPublisherLinks } from '@/hooks/tenancy/useOrganizationPublisherLinks';
import type { ContractOut } from '@/pages/tenancy/contracts/constants';
import { extractBackendMessage } from '@/utils/apiError';

interface ContractFormModalProps {
  open: boolean;
  onClose: () => void;
  record: OrganizationOut;
  /** When set, the modal edits this contract; otherwise it creates one. */
  contract: ContractOut | null;
}

export const ContractFormModal = ({
  open,
  onClose,
  record,
  contract,
}: ContractFormModalProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const { mutateAsync: createContract } = useCreate();
  const { mutateAsync: updateContract } = useUpdate();
  const [form] = Form.useForm<ContractFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const isEdit = contract != null;
  const tk = (suffix: string) => t(`tenancy.contracts.${suffix}`);

  // Contracts may only target the organization's own publishers — the
  // backend checks membership on the contract's start.
  const { publisherIds } = useOrganizationPublisherLinks(record.id, {
    enabled: open,
  });
  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    // The tree offers only what comes back, and `getMany` would otherwise
    // fall back to the server's default of 100.
    meta: { params: { limit: PUBLISHER_TREE_LIMIT } },
    queryOptions: { enabled: open && publisherIds.length > 0 },
  });
  const publisherTreeData = toPublisherTreeData(
    buildPublisherTree(publishersResult?.data ?? [])
  );

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setSubmitting(false);
      return;
    }
    if (contract) {
      form.setFieldsValue(toContractFormValues(contract));
    } else {
      form.setFieldsValue({ rates: [{ ...CONTRACT_STARTER_RATE }] });
    }
  }, [open, contract, form]);

  const handleSubmit = async () => {
    let values: ContractFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    try {
      if (contract) {
        await updateContract({
          resource: 'tenancy.contracts',
          id: contract.id,
          values: toContractPayload(values),
          meta: { method: 'patch' },
          successNotification: false,
          errorNotification: false,
        });
      } else {
        await createContract({
          resource: 'tenancy.contracts',
          values: {
            organization_id: record.id,
            ...toContractPayload(values),
          },
          successNotification: false,
          errorNotification: false,
        });
      }
      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: tk(
          isEdit ? 'modal.success_update' : 'modal.success_create'
        ),
      });
      onClose();
    } catch (error) {
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description: extractBackendMessage(error) ?? tk('modal.error_fallback'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={tk(isEdit ? 'modal.edit_title' : 'modal.create_title')}
      okText={tk(isEdit ? 'modal.submit_save' : 'modal.submit_create')}
      okButtonProps={{ loading: submitting }}
      cancelText={t('common.cancel')}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={!submitting}
      onCancel={onClose}
      onOk={handleSubmit}
      destroyOnClose
      // Each rate row packs five fields — 640px squeezed them to ~115px.
      width="90%"
      style={{ maxWidth: 1040 }}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={submitting}
      >
        <ContractFormFields publisherTreeData={publisherTreeData} />
      </Form>
    </Modal>
  );
};
